import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { requireAdmin } from "@/auth/requireAdmin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormGroupDefault,
  PageContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "beproduct-ui";
import { api } from "@/lib/api";
import { ScheduleCard } from "@/components/schedule-card";
import { PageHeader } from "@/components/navigation/page-header";

interface PipelineSpec { key: string; name: string; description: string; cron: string; steps: string[] }
interface ModuleSpec { key: string; displayName: string; emoji: string; description: string }

type Pick = "pipeline" | "module" | "custom";

export const Route = createFileRoute("/_authed/$companySlug/jobs/new")({
  beforeLoad: ({ context, params }) => {
    requireAdmin(context, params.companySlug);
  },
  component: NewJobPage,
});

function NewJobPage() {
  const { company } = Route.useRouteContext();
  const { companySlug } = Route.useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const pipelines = useQuery({
    queryKey: ["recommended-pipelines"],
    queryFn: () => api<{ pipelines: PipelineSpec[] }>("/api/modules/recommended-pipelines"),
  });
  const modules = useQuery({
    queryKey: ["modules"],
    queryFn: () => api<{ modules: ModuleSpec[] }>("/api/modules"),
  });

  const [pick, setPick] = useState<Pick>("pipeline");
  const [pipelineKey, setPipelineKey] = useState("");
  const [moduleKey, setModuleKey] = useState("");
  const [customName, setCustomName] = useState("");
  const [customSteps, setCustomSteps] = useState<string[]>([]);
  const [cron, setCron] = useState("0 0 * * *");
  const [tz, setTz] = useState("UTC");

  const m = useMutation({
    mutationFn: () =>
      api<{ job: { id: string } }>(`/api/tenants/${company.id}/jobs`, {
        method: "POST",
        body: JSON.stringify({
          pipeline_key: pick === "pipeline" ? pipelineKey : undefined,
          module_key: pick === "module" ? moduleKey : undefined,
          custom_pipeline: pick === "custom" ? customSteps : undefined,
          custom_name: pick === "custom" ? customName : undefined,
          schedule_cron: cron || undefined,
        }),
      }),
    onSuccess: () => {
      toast.success("Workflow created");
      qc.invalidateQueries({ queryKey: ["tenant", company.id] });
      nav({ to: "/$companySlug/jobs", params: { companySlug } });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const moduleByKey = (k: string): ModuleSpec | undefined =>
    modules.data?.modules.find((m) => m.key === k);

  const moveStep = (idx: number, dir: -1 | 1): void => {
    const arr = [...customSteps];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target]!, arr[idx]!];
    setCustomSteps(arr);
  };

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (pick === "pipeline" && !pipelineKey) {
      toast.error("Pick a pipeline");
      return;
    }
    if (pick === "module" && !moduleKey) {
      toast.error("Pick a module");
      return;
    }
    if (pick === "custom" && customSteps.length === 0) {
      toast.error("Add at least one step");
      return;
    }
    m.mutate();
  };

  return (
    <PageContent>
      <form onSubmit={submit} className="pt-4 pb-8 md:pt-6 space-y-4 max-w-3xl">
        <PageHeader title="New workflow" />

        <Card>
          <CardHeader><CardTitle>What should this workflow run?</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <ModeOption
                value="pipeline"
                current={pick}
                onClick={setPick}
                title="Recommended pipeline"
                subtitle="One of the eight presets"
              />
              <ModeOption
                value="module"
                current={pick}
                onClick={setPick}
                title="Single module"
                subtitle="Run one ETL module"
              />
              <ModeOption
                value="custom"
                current={pick}
                onClick={setPick}
                title="Custom pipeline"
                subtitle="Pick + order any modules"
              />
            </div>

            {pick === "pipeline" && (
              <div>
                <label className="text-sm font-medium mb-1 block">Pipeline</label>
                <Select value={pipelineKey} onValueChange={setPipelineKey}>
                  <SelectTrigger><SelectValue placeholder="Pick a pipeline" /></SelectTrigger>
                  <SelectContent>
                    {pipelines.data?.pipelines.map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        <div>
                          <div className="font-medium">{p.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {p.steps.join(" → ")}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {pick === "module" && (
              <div>
                <label className="text-sm font-medium mb-1 block">Module</label>
                <Select value={moduleKey} onValueChange={setModuleKey}>
                  <SelectTrigger><SelectValue placeholder="Pick a module" /></SelectTrigger>
                  <SelectContent>
                    {modules.data?.modules.map((mod) => (
                      <SelectItem key={mod.key} value={mod.key}>
                        {mod.emoji} {mod.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {pick === "custom" && (
              <div className="space-y-3">
                <FormGroupDefault
                  label="Workflow name"
                  placeholder="e.g. Nightly Material Refresh"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to auto-name as "Custom (N steps)".
                </p>

                <div className="rounded-md border p-3 bg-muted/30 space-y-2">
                  <div className="text-sm font-medium">
                    Steps ({customSteps.length})
                  </div>
                  {customSteps.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">
                      No steps yet. Add one below.
                    </p>
                  ) : (
                    <ol className="space-y-1.5">
                      {customSteps.map((k, idx) => {
                        const mod = moduleByKey(k);
                        return (
                          <li
                            key={`${k}-${idx}`}
                            className="flex items-center gap-2 bg-card border rounded-md px-3 py-2"
                          >
                            <span className="text-xs text-muted-foreground tabular-nums w-5">
                              #{idx + 1}
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="font-medium">
                                {mod?.emoji} {mod?.displayName ?? k}
                              </span>
                              <code className="text-xs text-muted-foreground ml-2 truncate">{k}</code>
                            </span>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => moveStep(idx, -1)}
                              disabled={idx === 0}
                            >
                              <ArrowUp className="size-4" />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => moveStep(idx, +1)}
                              disabled={idx === customSteps.length - 1}
                            >
                              <ArrowDown className="size-4" />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() =>
                                setCustomSteps(customSteps.filter((_, i) => i !== idx))
                              }
                            >
                              <X className="size-4" />
                            </Button>
                          </li>
                        );
                      })}
                    </ol>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <Select
                      value=""
                      onValueChange={(v) => v && setCustomSteps([...customSteps, v])}
                    >
                      <SelectTrigger className="flex-1">
                        <Plus className="size-4 mr-2" />
                        <SelectValue placeholder="Add a step…" />
                      </SelectTrigger>
                      <SelectContent>
                        {modules.data?.modules.map((mod) => (
                          <SelectItem key={mod.key} value={mod.key}>
                            {mod.emoji} {mod.displayName}{" "}
                            <span className="text-muted-foreground ml-2">{mod.key}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Steps run sequentially. A failed step is recorded as failed but
                    the next step still runs (continue-on-error).
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <ScheduleCard cron={cron} onCronChange={setCron} tz={tz} onTzChange={setTz} />

        <div className="flex gap-3">
          <Button type="submit" disabled={m.isPending}>
            {m.isPending ? "Creating…" : "Create workflow"}
          </Button>
          <Button asChild variant="ghost">
            <Link to="/$companySlug" params={{ companySlug }}>Cancel</Link>
          </Button>
        </div>
      </form>
    </PageContent>
  );
}

function ModeOption({
  value,
  current,
  onClick,
  title,
  subtitle,
}: {
  value: Pick;
  current: Pick;
  onClick: (v: Pick) => void;
  title: string;
  subtitle: string;
}) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`flex flex-col gap-1 border rounded-md p-3 text-left ${
        active ? "border-primary bg-primary/5" : ""
      }`}
    >
      <span className="font-medium text-sm">{title}</span>
      <span className="text-xs text-muted-foreground">{subtitle}</span>
    </button>
  );
}
